import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    Add as AddIcon,
    Cancel as CancelIcon,
    ColorLens as ColorIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    FormControl,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import { ButtonVariant } from "Constant/button";
import { capitalizeFirstLetter } from "Constant/string";
import { ChromePicker } from "react-color";

import StandartButton from "components/atoms/StandartButton";
import ImageUploadInput from "components/molecules/ImageUploadInput";
import MultipleImageUploadInput from "components/molecules/MultipleImageUploadInput";
import SwalAlertComp from "components/SwalAlert";

import GridSelector from "./components/GridSelector";

const StepEditor = ({
    steps,
    step = null,
    onSave,
    onCancel,
    customSources,
    selectedCustomSourceId,
    templates,
}) => {
    const funnelTemplate = useMemo(() => {
        return templates?.data?.find((template) => template?.name === "funnel");
    }, [templates]);
    const options = useMemo(() => {
        return funnelTemplate?.fields?.find((field) => field?.name === "steps")
            ?.options;
    }, [funnelTemplate]);

    const [editedStep, setEditedStep] = useState(null);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [colorPickerField, setColorPickerField] = useState("");
    const [validationErrors, setValidationErrors] = useState({});

    const populatedCustomSource = useMemo(
        () =>
            customSources?.data?.find(
                (source) => source?.id === selectedCustomSourceId,
            ),
        [selectedCustomSourceId, customSources?.data],
    );

    useEffect(() => {
        if (step) {
            setEditedStep({
                ...step,
                attributes: step.attributes || {},
            });
        }
    }, [step]);

    const handleBasicChange = (field, value) => {
        setEditedStep((prev) => ({
            ...prev,
            [field]: value,
        }));
        // Clear validation error when user types
        if (validationErrors[field]) {
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleAttributeChange = (field, value) => {
        setEditedStep((prev) => ({
            ...prev,
            attributes: {
                ...prev.attributes,
                [field]: value,
            },
        }));
        // Clear validation error when user types
        if (validationErrors[field]) {
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleAddOption = useCallback(() => {
        setEditedStep((prev) => ({
            ...prev,
            attributes: {
                ...prev.attributes,
                options: [...(prev.attributes.options || []), ""],
            },
        }));
    }, []);

    const handleOptionChange = useCallback((index, value, key) => {
        setEditedStep((prev) => ({
            ...prev,
            attributes: {
                ...prev.attributes,
                options: prev.attributes.options.map((option, i) =>
                    i === index ? value : option,
                ),
            },
        }));
        setValidationErrors((prev) => {
            const newErrors = { ...prev };

            // Clear the error for this specific option if it's filled
            if (value.trim() !== "") {
                delete newErrors[`${key}[${index}]`];
            } else {
                // Set error if the field is empty and it's required
                newErrors[`${key}[${index}]`] = `Option ${
                    index + 1
                } is required`;
            }

            return newErrors;
        });
    }, []);
    const handleCustomSourceChange = (value) => {
        setEditedStep((prev) => ({
            ...prev,
            attributes: {
                ...prev.attributes,
            },
            mapped_field_id: value,
        }));
    };

    const handleRemoveOption = useCallback((index, key) => {
        setEditedStep((prev) => ({
            ...prev,
            attributes: {
                ...prev.attributes,
                options: prev.attributes.options.filter((_, i) => i !== index),
            },
        }));
        setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[`${key}[${index}]`];
            return newErrors;
        });
    }, []);

    const handleColorChange = (color) => {
        handleAttributeChange(colorPickerField, color.hex);
    };

    const closeColorPicker = () => {
        setShowColorPicker(false);
    };

    const openColorPicker = (field) => {
        setColorPickerField(field);
        setShowColorPicker(true);
    };

    // Validation functions
    const validateStep = () => {
        const errors = {};

        // Validate basic fields
        if (!editedStep.title || editedStep.title.trim() === "") {
            errors.title = "Step title is required";
        }

        // Validate step attributes based on configuration
        const stepConfig = getStepTypeConfig(editedStep?.type);
        stepConfig.forEach((attr) => {
            if (attr.required) {
                const value = editedStep?.attributes?.[attr.key];
                if (
                    !value ||
                    (typeof value === "string" && value.trim() === "") ||
                    (Array.isArray(value) && value.length === 0)
                ) {
                    errors[attr.key] = `${attr.label} is required`;
                    SwalAlertComp({
                        title: "Failed",
                        message: `${attr.label} is required`,
                        icon: "error",
                        timer: 3000,
                    });
                }
                if (
                    attr.input_type === "input" &&
                    attr.type === "array" &&
                    attr.required
                ) {
                    value?.forEach((item, index) => {
                        if (!item || item.trim() === "") {
                            errors[`${attr.key}[${index}]`] =
                                "This field is required";
                        }
                    });
                }
            }
        });

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = () => {
        if (validateStep()) {
            onSave(editedStep);
        }
    };

    const getStepTypeConfig = (type) => {
        if (!type) return [];

        const stepTypeConfig = options.find((option) => option.type === type);
        if (!stepTypeConfig) return [];

        return stepTypeConfig.attributes.map((attr) => ({
            key: attr.code,
            label: attr.title,
            type: attr.type,
            input_type: attr.input_type,
            required: attr.required,
            placeholder: attr.placeholder,
            options: attr.options || [],
            attributes: attr.attributes || [],
        }));
    };

    const renderAttributeField = (attr) => {
        const value = editedStep?.attributes?.[attr.key] || "";
        const hasError = validationErrors[attr.key];
        const errorMessage = hasError;

        const handlers = [
            {
                predicate: () =>
                    attr.input_type === "input" && attr.type !== "array",
                handler: () => (
                    <TextField
                        fullWidth
                        label={attr.label}
                        value={value}
                        onChange={(e) =>
                            handleAttributeChange(attr.key, e.target.value)
                        }
                        required={attr.required}
                        type={attr.type === "number" ? "number" : "text"}
                        placeholder={attr.placeholder}
                        variant="outlined"
                        error={!!hasError}
                        helperText={errorMessage}
                    />
                ),
            },
            {
                predicate: () => attr.input_type === "select",
                handler: () => (
                    <Select
                        fullWidth
                        label={attr.label}
                        value={value}
                        onChange={(e) =>
                            handleAttributeChange(attr.key, e.target.value)
                        }
                        variant="outlined"
                        error={!!hasError}
                        helperText={errorMessage}
                    >
                        {attr.options.map((option) => (
                            <MenuItem key={option} value={option}>
                                {capitalizeFirstLetter(option)}
                            </MenuItem>
                        ))}
                    </Select>
                ),
            },
            {
                predicate: () => attr.input_type === "color-picker",
                handler: () => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        <TextField
                            fullWidth
                            label={attr.label}
                            value={value}
                            onChange={(e) =>
                                handleAttributeChange(attr.key, e.target.value)
                            }
                            variant="outlined"
                            error={!!hasError}
                            helperText={errorMessage}
                        />
                        <IconButton
                            onClick={() => openColorPicker(attr.key)}
                            sx={{
                                bgcolor: value || "#000000",
                                color: "white",
                                minWidth: 40,
                                height: 40,
                                "&:hover": {
                                    bgcolor: value || "#000000",
                                    transform: "scale(1.05)",
                                },
                                transition: "all 0.2s ease",
                            }}
                        >
                            <ColorIcon />
                        </IconButton>
                    </Box>
                ),
            },
            {
                predicate: () => attr.input_type === "checkbox",
                handler: () => (
                    <Box sx={{ p: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={value}
                                    onChange={(e) =>
                                        handleAttributeChange(
                                            attr.key,
                                            e.target.checked,
                                        )
                                    }
                                    color="primary"
                                />
                            }
                            label={
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500 }}
                                    >
                                        {attr.label}
                                    </Typography>
                                    {attr.required && (
                                        <Chip
                                            label="Required"
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            onClick={() => {}}
                                        />
                                    )}
                                </Box>
                            }
                        />
                    </Box>
                ),
            },
            {
                predicate: () =>
                    attr.type === "array" && attr.input_type === "image",
                handler: () => (
                    <MultipleImageUploadInput
                        value={value || []}
                        onChange={(newImages) =>
                            handleAttributeChange(attr.key, newImages)
                        }
                    />
                ),
            },
            {
                predicate: () => attr.type === "array" && attr.key === "images",
                handler: () => (
                    <MultipleImageUploadInput
                        value={value || []}
                        onChange={(newImages) =>
                            handleAttributeChange(attr.key, newImages)
                        }
                    />
                ),
            },
            {
                predicate: () =>
                    attr.type === "array" &&
                    (attr.key === "grid_items" ||
                        attr.key === "processing_steps"),
                handler: () => (
                    <GridSelector
                        value={value || []}
                        attribute={attr}
                        handleAttributeChange={handleAttributeChange}
                        title={
                            attr.key === "processing_steps"
                                ? "Processing Steps"
                                : "Grid Items"
                        }
                        noItemsText={
                            attr.key === "processing_steps"
                                ? "No processing steps added yet. Click the + button to add your first step."
                                : "No grid items added yet. Click the + button to add your first item."
                        }
                    />
                ),
            },
            {
                predicate: () =>
                    attr.type === "string" &&
                    attr.input_type === "multi-select",
                handler: () => (
                    <Select
                        fullWidth
                        label={attr.label}
                        value={value}
                        onChange={(e) =>
                            handleAttributeChange(attr.key, e.target.value)
                        }
                        variant="outlined"
                    >
                        {attr.options.map((option) => (
                            <MenuItem key={option} value={option}>
                                {capitalizeFirstLetter(option)}
                            </MenuItem>
                        ))}
                    </Select>
                ),
            },
            {
                predicate: () =>
                    attr.type === "array" && attr.input_type === "input",
                handler: () => (
                    <>
                        <Box>
                            {(value || []).length > 0 ? (
                                <Stack spacing={1.5}>
                                    {(value || []).map((item, index) => (
                                        <TextField
                                            label={`Option ${index + 1}`}
                                            fullWidth
                                            value={item}
                                            onChange={(e) =>
                                                handleOptionChange(
                                                    index,
                                                    e.target.value,
                                                    attr.key,
                                                )
                                            }
                                            error={
                                                !!validationErrors[
                                                    `${attr.key}[${index}]`
                                                ]
                                            }
                                            helperText={
                                                validationErrors[
                                                    `${attr.key}[${index}]`
                                                ]
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        onClick={() =>
                                                            handleRemoveOption(
                                                                index,
                                                                attr.key,
                                                            )
                                                        }
                                                        sx={{
                                                            mr: -1,
                                                            color: "text.secondary",
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                ),
                                            }}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <Box
                                    sx={{
                                        textAlign: "center",
                                        py: 4,
                                        border: "2px dashed",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        bgcolor: "grey.50",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: "50%",
                                            bgcolor: "grey.300",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mx: "auto",
                                            mb: 1,
                                        }}
                                    >
                                        <AddIcon sx={{ color: "grey.600" }} />
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "text.primary",
                                            mb: 0.5,
                                        }}
                                    >
                                        No options yet
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "text.secondary",
                                        }}
                                    >
                                        Click 'Add Option' to get started.
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Footer */}
                        <Box
                            sx={{
                                bgcolor: "background.paper",
                                mt: 2,
                            }}
                        >
                            <StandartButton
                                startIcon={<AddIcon />}
                                onClick={handleAddOption}
                                variant={ButtonVariant.BLACK_OUTLINED}
                                fullWidth
                                sx={{
                                    borderRadius: 1,
                                    textTransform: "none",
                                    fontWeight: 500,
                                }}
                            >
                                Add Option
                            </StandartButton>
                        </Box>
                    </>
                ),
            },
            {
                predicate: () => attr.input_type === "html-editor",
                handler: () => (
                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 1,
                                fontWeight: 500,
                                color: "text.primary",
                            }}
                        >
                            {attr.label}
                            {attr.required && (
                                <Typography
                                    component="span"
                                    sx={{ color: "error.main", ml: 0.5 }}
                                >
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={8}
                            value={value || ""}
                            onChange={(e) =>
                                handleAttributeChange(attr.key, e.target.value)
                            }
                            placeholder="Enter HTML content..."
                            error={!!hasError}
                            helperText={
                                errorMessage ||
                                "Enter HTML content for the success page"
                            }
                            sx={{
                                "& .MuiInputBase-input": {
                                    fontFamily: "monospace",
                                    fontSize: "0.875rem",
                                },
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                mt: 0.5,
                                display: "block",
                            }}
                        >
                            This HTML will be displayed on the success page
                            after form submission.
                        </Typography>
                    </Box>
                ),
            },
        ];

        return handlers.find((handler) => handler.predicate())?.handler();
    };
    if (!editedStep) {
        return (
            <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary">
                    Select a step to edit
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                width: "100%",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 3,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        mb: 2,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                mb: 0.5,
                            }}
                        >
                            Edit Step
                        </Typography>
                        <Tooltip
                            title={editedStep.title || "Untitled"}
                            placement="bottom-start"
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    color: "text.secondary",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: "300px",
                                    cursor: "help",
                                }}
                            >
                                {editedStep.title || "Untitled"}
                            </Typography>
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CancelIcon />}
                            onClick={onCancel}
                            size="small"
                            style={{
                                minWidth: "unset !important",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            size="small"
                            style={{
                                minWidth: "unset !important",
                            }}
                            color="primary"
                            variant="contained"
                        >
                            Save Step
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Editor Content */}
            <Box
                sx={{ flex: 1, overflow: "auto", p: 3 }}
                className="funnel-scrollbar"
            >
                <Box>
                    {/* Basic Information Section */}
                    <Box sx={{ mb: 4 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 3,
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: "text.primary",
                                }}
                            >
                                Basic Information
                            </Typography>
                        </Box>

                        <Stack gap={2}>
                            <TextField
                                fullWidth
                                label="Step Title"
                                value={editedStep.title || ""}
                                onChange={(e) =>
                                    handleBasicChange("title", e.target.value)
                                }
                                required
                                variant="outlined"
                                error={!!validationErrors.title}
                                helperText={validationErrors.title}
                            />
                            <Box sx={{ position: "relative" }} marginTop={1}>
                                <textarea
                                    value={editedStep.description || ""}
                                    onChange={(e) =>
                                        handleBasicChange(
                                            "description",
                                            e.target.value,
                                        )
                                    }
                                    placeholder="Enter step description..."
                                    style={{
                                        width: "100%",
                                        minHeight: "80px",
                                        maxHeight: "200px",
                                        padding: "12px 14px",
                                        border: "1px solid #c4c4c4",
                                        borderRadius: "4px",
                                        fontSize: "16px",
                                        fontFamily: "inherit",
                                        lineHeight: "1.5",
                                        resize: "vertical",
                                        outline: "none",
                                        transition: "border-color 0.2s ease",
                                        backgroundColor: "transparent",
                                        boxSizing: "border-box",
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = "#1976d2";
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = "#c4c4c4";
                                    }}
                                />
                            </Box>
                            <Box>
                                <ImageUploadInput
                                    value={editedStep.image}
                                    logoUrl={editedStep.image_url}
                                    onChange={(value) =>
                                        handleBasicChange("image", value)
                                    }
                                    onLogoUrlChange={(value) =>
                                        handleBasicChange("image_url", value)
                                    }
                                />
                            </Box>
                            {!!customSources?.data?.length &&
                                !customSources?.isLoadingList &&
                                populatedCustomSource && (
                                    <Box sx={{ mb: 2 }}>
                                        <FormControl fullWidth>
                                            <Select
                                                label="Map Custom Source Field"
                                                displayEmpty
                                                fullWidth
                                                value={
                                                    editedStep?.mapped_field_id ||
                                                    ""
                                                }
                                                onChange={(e) =>
                                                    handleCustomSourceChange(
                                                        e.target.value,
                                                    )
                                                }
                                                MenuProps={{
                                                    anchorOrigin: {
                                                        vertical: "top",
                                                        horizontal: "left",
                                                    },
                                                    transformOrigin: {
                                                        vertical: "bottom",
                                                        horizontal: "left",
                                                    },
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: "200px",
                                                        },
                                                    },
                                                }}
                                            >
                                                <MenuItem value="">
                                                    No field mapping (optional)
                                                </MenuItem>
                                                {populatedCustomSource?.fields?.map(
                                                    (customField) => {
                                                        const isAlreadyMapped =
                                                            steps?.some(
                                                                (step) =>
                                                                    step.mapped_field_id ===
                                                                        customField.id &&
                                                                    editedStep.id !==
                                                                        step.id,
                                                            );

                                                        return (
                                                            <MenuItem
                                                                key={
                                                                    customField.id
                                                                }
                                                                value={
                                                                    customField.id
                                                                }
                                                                disabled={
                                                                    isAlreadyMapped
                                                                }
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    justifyContent:
                                                                        "space-between",
                                                                    alignItems:
                                                                        "center",
                                                                }}
                                                            >
                                                                <span>
                                                                    {
                                                                        customField.name
                                                                    }
                                                                </span>
                                                                {isAlreadyMapped && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            ml: 1,
                                                                            color: "text.secondary",
                                                                            fontSize:
                                                                                "0.7rem",
                                                                            fontStyle:
                                                                                "italic",
                                                                        }}
                                                                    >
                                                                        (already
                                                                        mapped)
                                                                    </Typography>
                                                                )}
                                                            </MenuItem>
                                                        );
                                                    },
                                                )}
                                            </Select>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    mt: 0.5,
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                Optionally map a custom source
                                                field to this step. Each field
                                                can only be mapped to one step.
                                            </Typography>
                                        </FormControl>
                                    </Box>
                                )}

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    height: "100%",
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={
                                                editedStep.optional || false
                                            }
                                            onChange={(e) =>
                                                handleBasicChange(
                                                    "optional",
                                                    e.target.checked,
                                                )
                                            }
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            <Typography variant="body2">
                                                Optional Step
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Stack>
                    </Box>
                    {/* Step Attributes Section */}
                    <Box sx={{ mb: 4, width: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 3,
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: "text.primary",
                                }}
                            >
                                Step Configuration
                            </Typography>
                        </Box>

                        <Stack gap={2}>
                            {getStepTypeConfig(editedStep?.type)?.map(
                                (attr) => (
                                    <Box key={attr.key}>
                                        {attr && renderAttributeField(attr)}
                                    </Box>
                                ),
                            )}
                        </Stack>
                    </Box>
                </Box>
            </Box>

            {showColorPicker && (
                <Box
                    onClick={closeColorPicker}
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                        cursor: "pointer",
                    }}
                >
                    <Paper
                        onClick={(e) => e.stopPropagation()}
                        sx={{ p: 2, cursor: "default" }}
                    >
                        <ChromePicker
                            color={
                                editedStep?.attributes?.[colorPickerField] ||
                                "#000000"
                            }
                            onChange={handleColorChange}
                        />
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mt: 2,
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={closeColorPicker}
                                size="small"
                                sx={{
                                    bgcolor: "#ed6c02",
                                    "&:hover": {
                                        bgcolor: "#c55a02",
                                    },
                                }}
                            >
                                Done
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            )}
        </Box>
    );
};

export default StepEditor;
